import React, { useEffect, useState } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Map,
  ShieldCheck,
  Smartphone,
  Hash,
  Sparkles,
  Loader2,
  IndianRupee,
  Box,
  AlertCircle,
  RotateCw,
  MessageSquare,
  PencilLine,
} from "lucide-react";
import { FormInput } from "./FormInput";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import axios from "axios";

import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config/api";

export const StepProfile = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
}) => {
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Using items-start in the grid ensures that if one input grows due to an error, 
          it doesn't stretch the height of the neighboring input's background. 
      */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-x-5 gap-y-2">
        <div className="flex flex-col">
          <FormInput
            label="Full Name"
            name="fullName"
            icon={User}
            placeholder="e.g. John Doe"
            value={values.fullName}
            onChange={handleChange}
            error={errors.fullName}
            touched={touched.fullName}
          />
        </div>
      </div>

      <div className="flex flex-col">
        <FormInput
          label="Mobile Number"
          name="mobile"
          icon={Phone}
          placeholder="10-digit number"
          value={values.mobile}
          onChange={(e) =>
            setFieldValue(
              "mobile",
              e.target.value.replace(/\D/g, "").slice(0, 10),
            )
          }
          error={errors.mobile}
          touched={touched.mobile}
        />
      </div>

      <div className="w-full">
        <FormInput
          label="Email Address"
          name="email"
          icon={Mail}
          placeholder="john@example.com"
          value={values.email}
          onChange={handleChange}
          error={errors.email}
          touched={touched.email}
        />
      </div>

      {/* Location Section Card */}
      <div className="bg-slate-50/80 backdrop-blur-sm p-5 rounded-[1.5rem] border border-slate-200/60 shadow-sm space-y-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="p-1.5 bg-emerald-100 rounded-lg">
            <MapPin size={14} className="text-emerald-600" />
          </div>
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
            Delivery & Location
          </span>
        </div>

        <FormInput
          label="Pincode"
          name="pincode"
          icon={MapPin}
          placeholder="6-digit code"
          value={values.pincode}
          onChange={(e) =>
            setFieldValue(
              "pincode",
              e.target.value.replace(/\D/g, "").slice(0, 6),
            )
          }
          error={errors.pincode}
          touched={touched.pincode}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormInput
            disabled
            label="District"
            name="district"
            icon={Map}
            value={values.district || ""}
            placeholder="Auto-detected"
            className="bg-white/70"
            // ✅ Added error handling props below
            error={errors.district}
            touched={touched.district}
          />
          <FormInput
            disabled
            label="State"
            name="state"
            icon={ShieldCheck}
            value={values.state || ""}
            placeholder="Auto-detected"
            className="bg-white/70"
            // ✅ Added error handling props below
            error={errors.state}
            touched={touched.state}
          />
        </div>
      </div>
    </div>
  );
};

export const StepOtp = ({
  values,
  setFieldValue,
  otpRefs,
  isVerified,
  setIsVerified,
  setActiveStep, // Ensure you pass this prop from parent
  sendOtp, // Ensure you pass this prop from parent
}) => {
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  
  let displayMobile=null;
  if(localStorage.getItem("customer_data")){
    let d= JSON.parse(localStorage.getItem("customer_data"));
    displayMobile= d?.mobile;
  }
  const handleEditNumber = () => {
    localStorage.removeItem("customer_data");
    setIsVerified(false);
    setActiveStep(0); // Redirect to Profile Step
  };

  const handleResend = async () => {
    setResending(true);
    const success = await sendOtp(values.mobile);
    if (success) {
      setFieldValue("otp", ""); // Clear current OTP inputs
      otpRefs.current[0]?.focus();
    }
    setResending(false);
  };

  const verifyOtp = async () => {
    if ((values.otp || "").length !== 6) {
      toast.error("Please enter 6-digit code");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(
        "https://uatwarranty.goelectronix.co.in/api/warranty/verify-customer-wa-otp",
        {
          mobile: values.mobile,
          otp: values.otp,
          company_id: 3,
        },
      );

      if (res.data.success) {
        setIsVerified(true);
        toast.success("OTP Verified!");
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch (error) {
      toast.error("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-white p-2 rounded-[2rem] border-2 border-slate-100 shadow-xl shadow-slate-100">
        {/* Header with Edit Option */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mb-3">
            <MessageSquare size={24} />
          </div>
          <h3 className="text-lg font-bold text-slate-800">Verify Number</h3>

          <div className="flex items-center gap-2 mt-2 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
            <span className="text-sm font-bold text-slate-600">
              +91 {displayMobile || values.mobile}
            </span>
            <button
              type="button"
              onClick={handleEditNumber}
              className="p-1 hover:bg-white hover:text-emerald-600 rounded-md transition-all text-slate-400"
              title="Edit Number"
            >
              <PencilLine size={14} />
            </button>
          </div>
        </div>

        {/* OTP Inputs */}
        <div className="grid grid-cols-6 gap-2 mb-8">
          {[...Array(6)].map((_, i) => (
            <input
              key={i}
              ref={(el) => (otpRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength="1"
              disabled={isVerified || loading}
              className={`h-12 text-center text-xl font-black rounded-xl border-2 transition-all outline-none 
                ${
                  isVerified
                    ? "bg-emerald-50 border-gray-200 text-emerald-600"
                    : "focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 border-slate-200 bg-slate-50/50"
                }`}
              value={(values.otp || "")[i] || ""}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                let arr = (values.otp || "").split("");
                arr[i] = val;
                setFieldValue("otp", arr.join(""));
                if (val && i < 5) otpRefs.current[i + 1].focus();
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !values.otp[i] && i > 0) {
                  otpRefs.current[i - 1].focus();
                }
              }}
            />
          ))}
        </div>

        {/* Action Buttons */}
        {!isVerified ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={verifyOtp}
              disabled={loading || (values.otp || "").length !== 6}
              className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                "Verify Code"
              )}
            </button>

            <button
              type="button"
              onClick={handleResend}
              disabled={resending || loading}
              className="w-full flex items-center justify-center gap-2 text-xs font-bold text-slate-400 hover:text-emerald-600 uppercase tracking-widest transition-colors"
            >
              <RotateCw size={12} className={resending ? "animate-spin" : ""} />
              {resending ? "Sending..." : "Resend code via WhatsApp"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 py-4 bg-emerald-50 text-emerald-600 rounded-2xl font-bold text-sm border border-emerald-100 animate-in zoom-in-95 duration-300">
            ✅ Verified Successfully
          </div>
        )}
      </div>
    </div>
  );
};

// --- STEP 2: DEVICE (Corrected) ---
export const StepDevice = ({
  values,
  setFieldValue,
  handleChange,
  errors,
  touched,
}) => {
  /**
   * Fetches data from API.
   * defaultOptions={true} triggers this with an empty searchTerm on mount.
   */
  const loadDeviceOptions = async (inputValue) => {
    const searchTerm = inputValue || "";

    try {
      const response = await axios.get(
        `${BASE_URL}warranty/device-models?search=${searchTerm}`,
      );

      // Based on your specific API structure: response -> data -> data -> data
      const deviceList = response.data?.data?.data || [];

      return deviceList.map((device) => ({
        value: device.id,
        label: device.name,
        brand: device.brand_name || "Smart Device",
        price: device.price || 0,
        categoryId: device.category_id, // Important for Step 3 (Plan)
      }));
    } catch (error) {
      console.error("Error fetching devices:", error);
      return [];
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* --- Search Field --- */}
      <div className="mb-4">
        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">
          Select Device Model
        </label>

        <AsyncSelect
          cacheOptions
          defaultOptions={true}
          loadOptions={loadDeviceOptions}
          className="mt-1.5"
          placeholder="Select or type to search..."
          menuPortalTarget={document.body}
          noOptionsMessage={() => "No models found"}
          loadingMessage={() => "Searching devices..."}
          isClearable
          onChange={(opt) => {
            if (opt) {
              // Set multiple Formik fields at once
              setFieldValue("deviceModel", opt.label);
              setFieldValue("brand", opt.brand);
              setFieldValue("categoryId", opt.categoryId);

              // Only auto-fill price if the API returns a positive value
              if (opt.price > 0) {
                setFieldValue("price", opt.price);
              }
            }
          }}
          styles={{
            control: (base) => ({
              ...base,
              borderRadius: "1rem",
              padding: "2px",
              border: "2px solid #f1f5f9",
              boxShadow: "none",
              "&:hover": { borderColor: "#10b981" },
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({
              ...base,
              borderRadius: "1rem",
              overflow: "hidden",
              marginTop: "4px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
            }),
            option: (base, { isFocused, isSelected }) => ({
              ...base,
              backgroundColor: isSelected
                ? "#10b981"
                : isFocused
                  ? "#f0fdf4"
                  : "white",
              color: isSelected ? "white" : "#334155",
              fontSize: "12px",
              padding: "12px 15px",
              cursor: "pointer",
              "&:active": { backgroundColor: "#059669" },
            }),
          }}
        />
        {touched.deviceModel && errors.deviceModel && (
          <p className="text-[10px] text-red-500 mt-1 ml-1">
            {errors.deviceModel}
          </p>
        )}
      </div>

      {/* --- Selected Device Preview Card --- */}
      {values.deviceModel && (
        <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100 relative overflow-hidden transition-all shadow-sm animate-in zoom-in-95">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <Box size={12} className="text-emerald-600" />
              <p className="text-[9px] font-bold uppercase text-emerald-600 tracking-widest">
                {values.brand}
              </p>
            </div>
            <p className="text-lg font-black text-slate-700 leading-tight">
              {values.deviceModel}
            </p>
          </div>
          <Sparkles
            className="absolute -right-4 -bottom-4 opacity-10 text-emerald-500"
            size={80}
          />
        </div>
      )}

      {/* --- Price & IMEI Details --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormInput
          label="Purchase Price"
          name="price"
          type="number"
          icon={IndianRupee}
          value={values.price}
          onChange={handleChange}
          error={errors.price}
          touched={touched.price}
          placeholder="e.g. 45000"
        />
        <FormInput
          label="IMEI Number"
          name="imei"
          icon={Smartphone}
          value={values.imei}
          onChange={handleChange}
          error={errors.imei}
          touched={touched.imei}
          placeholder="16 digit IMEI"
        />
      </div>

      <div className="grid grid-cols-1">
        <FormInput
          label="Serial Number (Required)"
          name="serial"
          icon={Hash}
          value={values.serial}
          onChange={handleChange}
          error={errors.serial}
          touched={touched.serial}
          placeholder="16 character Serial Number"
        />
      </div>
    </div>
  );
};

// --- STEP 3: PLAN ---
export const StepPlan = ({ values, setFieldValue }) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);

        const payload = {
          product_price: Number(values.price),
          category_id: values.categoryId,
          company_id: 3,
          retailer_id: 4,
          product_id: 28, // Removed hardcoded ID to allow dynamic fetching
        };

        const response = await axios.post(
          `${BASE_URL}warranty/public-price-templates`,
          payload,
        );

        if (response.data.status === false) {
          setError(response.data.message || "No templates found.");
          setTemplates([]);
          return;
        }

        setTemplates(response.data?.data || []);
      } catch (err) {
        setError(
          err?.response?.data?.message || "Failed to connect to server.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (values.categoryId && values.price) fetchPlans();
  }, [values.categoryId, values.price]);

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Loader2 className="animate-spin mb-2" size={32} />
        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">
          Finding best plans...
        </p>
      </div>
    );

  if (error)
    return (
      <div className="p-6 rounded-2xl border-2 border-dashed border-slate-100 text-center">
        <AlertCircle className="mx-auto text-amber-500 mb-2" size={24} />
        <p className="text-xs text-slate-500 font-medium">{error}</p>
      </div>
    );

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300 overflow-auto">
      {templates.map((plan) => (
        <div
          key={plan.id}
          onClick={() => {
            // Updated to set all required fields from the API response
            setFieldValue("productId", plan.warranty_product_id);
            setFieldValue("package", plan.warranty_product?.name);
            setFieldValue("planPrice", plan.company_payout);

            // --- NEW FIELDS ---
            // Set validity (e.g., 183 or 91)
            setFieldValue("validity_days", plan.warranty_product?.validity);
            // Set claims (e.g., 50)
            setFieldValue("claims", plan.warranty_product?.claims);
          }}
          className={`cursor-pointer p-4 rounded-2xl border-2 transition-all duration-300 grid grid-cols-[auto_1fr_auto] items-center gap-3 overflow-hidden ${
            // Use productId for the "active" check as it's more reliable than price
            values.productId === plan.warranty_product_id
              ? "border-emerald-500 bg-emerald-50/50 shadow-md shadow-emerald-100"
              : "border-slate-100 bg-slate-50 hover:border-emerald-200"
          }`}
        >
          {/* 1. Icon */}
          <div
            className={`p-3 rounded-xl shrink-0 ${values.productId === plan.warranty_product_id ? "bg-emerald-600 text-white" : "bg-white text-slate-400 shadow-sm"}`}
          >
            <ShieldCheck size={20} />
          </div>

          {/* 2. Middle Content */}
          <div className="min-w-0 w-full overflow-hidden">
            <p className="font-black text-[11px] leading-tight text-slate-800 uppercase tracking-tight break-words line-clamp-2 italic">
              {plan.warranty_product?.name}
            </p>
            <div className="flex flex-wrap gap-1 mt-1.5">
              <span className="text-[7px] font-extrabold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded uppercase">
                {plan.product_type}
              </span>
              {/* Added Validity Badge for User Visibility */}
              <span className="text-[7px] font-extrabold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded uppercase">
                {plan.warranty_product?.validity} Days Validity
              </span>
            </div>
          </div>

          {/* 3. Price */}
          <div className="text-right shrink-0 border-l border-slate-200 pl-3 min-w-[70px]">
            <p className="text-sm font-black text-emerald-600 leading-none">
              ₹{plan.company_payout}
            </p>
            <p className="text-[7px] font-bold text-slate-400 uppercase mt-1">
              One-Time
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// import React, { useEffect, useState } from "react";
// import {
//   User,
//   Phone,
//   Mail,
//   MapPin,
//   Map,
//   ShieldCheck,
//   MessageSquare,
//   Smartphone,
//   Hash,
//   Sparkles,
//   Loader2,
//   RotateCcw,
//   CheckCircle2,
//   IndianRupee,
//   Box,
//   AlertCircle,
// } from "lucide-react";
// import { FormInput } from "./FormInput";
// import Select from "react-select";
// import AsyncSelect from "react-select/async";
// import axios from "axios";

// import { toast } from "react-hot-toast";
// import { BASE_URL } from "../../config/api";

// export const StepProfile = ({
//   values,
//   errors,
//   touched,
//   handleChange,
//   setFieldValue,
//   otpRefs,
//   isVerified, // Received from CustomerSignup
//   setIsVerified, // Received from CustomerSignup
// }) => {
//   const [isSending, setIsSending] = useState(false);
//   const [isVerifying, setIsVerifying] = useState(false);
//   const [timer, setTimer] = useState(0);

//   const mobileNumber = values.mobile;
//   const showOtp = mobileNumber.length === 10;

//   // Timer logic for Resend button
//   useEffect(() => {
//     let interval = null;
//     if (timer > 0) {
//       interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
//     }
//     return () => clearInterval(interval);
//   }, [timer]);

//   // Auto-send OTP when mobile hits 10 digits
//   useEffect(() => {
//     if (mobileNumber.length === 10 && timer === 0 && !isVerified) {
//       sendOtp(mobileNumber);
//     }
//   }, [mobileNumber]);

//   const sendOtp = async (mobile) => {
//     if (isSending || isVerified) return;
//     try {
//       setIsSending(true);
//       await axios.post(
//         "https://uatwarranty.goelectronix.co.in/api/warranty/send-customer-wa-otp",
//         {
//           mobile,
//           company_id: 2,
//         },
//       );
//       setTimer(30);
//       toast.success("OTP sent to WhatsApp");
//     } catch (error) {
//       toast.error("Failed to send OTP");
//     } finally {
//       setIsSending(false);
//     }
//   };

//   const handleVerifyInternal = async () => {
//     if (values.otp.length !== 6) return;
//     try {
//       setIsVerifying(true);
//       const res = await axios.post(
//         "https://uatwarranty.goelectronix.co.in/api/warranty/verify-customer-wa-otp",
//         {
//           mobile: values.mobile,
//           otp: values.otp,
//           company_id: 2,
//         },
//       );

//       if (res.data.success) {
//         setIsVerified(true); // Updates the parent state
//         toast.success("OTP Verified Successfully!");
//       } else {
//         toast.error("Invalid OTP");
//       }
//     } catch (err) {
//       toast.error("Verification failed");
//     } finally {
//       setIsVerifying(false);
//     }
//   };

//   return (
//     <div className="space-y-1 animate-in fade-in slide-in-from-bottom-2 duration-300">
//       {/* Name Field */}
//       <FormInput
//         label="Name"
//         name="fullName"
//         icon={User}
//         value={values.fullName}
//         onChange={handleChange}
//         error={errors.fullName}
//         touched={touched.fullName}
//         placeholder="e.g. John Doe"
//       />

//       {/* Mobile Field */}
//       <FormInput
//         label="Mobile"
//         name="mobile"
//         icon={Phone}
//         value={values.mobile}
//         onChange={(e) => {
//           setIsVerified(false); // Reset verification if number changes
//           setFieldValue(
//             "mobile",
//             e.target.value.replace(/\D/g, "").slice(0, 10),
//           );
//         }}
//         error={errors.mobile}
//         touched={touched.mobile}
//         placeholder="10 digits"
//       />

//       {/* OTP Section */}
//       {showOtp && !isVerified && (
//         <div
//           className={`animate-in zoom-in-95 duration-300 mb-6 p-4 rounded-2xl border transition-all ${isVerified ? "bg-emerald-50 border-emerald-500" : "bg-slate-50/50 border-slate-100"}`}
//         >
//           <div className="flex items-center justify-between mb-4">
//             <div className="flex items-center gap-2">
//               {isVerified ? (
//                 <CheckCircle2 size={14} className="text-emerald-600" />
//               ) : (
//                 <MessageSquare size={14} className="text-slate-400" />
//               )}
//               <p
//                 className={`text-[10px] font-bold ${isVerified ? "text-emerald-800" : "text-slate-500"}`}
//               >
//                 {isVerified
//                   ? "Phone Number Verified"
//                   : isSending
//                     ? "Sending..."
//                     : "Enter WhatsApp OTP"}
//               </p>
//             </div>

//             {!isVerified &&
//               (timer > 0 ? (
//                 <span className="text-[9px] font-black text-slate-400 tabular-nums">
//                   Resend in {timer}s
//                 </span>
//               ) : (
//                 <button
//                   type="button"
//                   onClick={() => sendOtp(mobileNumber)}
//                   className="text-[9px] font-black text-emerald-600 uppercase tracking-wider flex items-center gap-1"
//                 >
//                   <RotateCcw size={10} /> Resend
//                 </button>
//               ))}
//           </div>

//           <div className="grid grid-cols-6 gap-2 mb-4">
//             {[...Array(6)].map((_, i) => (
//               <input
//                 key={i}
//                 ref={(el) => (otpRefs.current[i] = el)}
//                 maxLength="1"
//                 disabled={isVerified}
//                 className={`w-full h-10 text-center font-black rounded-lg border outline-none transition-all ${isVerified ? "bg-emerald-100/50 border-emerald-200 text-emerald-700" : "bg-white border-slate-200 focus:border-emerald-500"}`}
//                 onChange={(e) => {
//                   const val = e.target.value;
//                   let otpArray = (values.otp || "").split("");
//                   otpArray[i] = val;
//                   setFieldValue("otp", otpArray.join(""));
//                   if (val && i < 5) otpRefs.current[i + 1].focus();
//                 }}
//               />
//             ))}
//           </div>

//           {!isVerified && (
//             <button
//               type="button"
//               onClick={handleVerifyInternal}
//               disabled={values.otp.length !== 6 || isVerifying}
//               className="w-full py-2 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2"
//             >
//               {isVerifying ? (
//                 <Loader2 size={14} className="animate-spin" />
//               ) : (
//                 "Verify OTP"
//               )}
//             </button>
//           )}
//         </div>
//       )}

//       {/* Email & Pincode */}
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
//         <FormInput
//           label="Email"
//           name="email"
//           icon={Mail}
//           value={values.email}
//           onChange={handleChange}
//           error={errors.email}
//           touched={touched.email}
//           placeholder="email@example.com"
//         />
//         <FormInput
//           label="Pincode"
//           name="pincode"
//           icon={MapPin}
//           value={values.pincode}
//           onChange={(e) => {
//             const val = e.target.value.replace(/\D/g, "").slice(0, 6);
//             setFieldValue("pincode", val);

//             // Reset district/state when user starts typing again
//             if (val.length < 6) {
//               setFieldValue("district", "");
//               setFieldValue("state", "");
//             }
//           }}
//           error={errors.pincode}
//           touched={touched.pincode}
//           placeholder="6 digits"
//         />
//       </div>

//       {/* District & State (Auto-filled by PincodeObserver) */}
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
//         <FormInput
//           disabled={true} // Keep disabled so user can't type manually
//           label="District"
//           name="district"
//           icon={Map}
//           value={values.district}
//           error={errors.district} // Formik will show "Pincode does not match" here
//           touched={touched.district} // ensures error shows after interaction
//           readOnly
//           placeholder={
//             values.pincode?.length === 6 && !values.district
//               ? "Invalid Pincode"
//               : "Auto-filled"
//           }
//         />

//         <FormInput
//           disabled={true}
//           label="State"
//           name="state"
//           icon={ShieldCheck}
//           value={values.state}
//           error={errors.state}
//           touched={touched.state}
//           readOnly
//           placeholder={
//             values.pincode?.length === 6 && !values.state
//               ? "Invalid Pincode"
//               : "Auto-filled"
//           }
//         />
//       </div>
//     </div>
//   );
// };

// // --- STEP 2: DEVICE (Corrected) ---
// export const StepDevice = ({
//   values,
//   setFieldValue,
//   handleChange,
//   errors,
//   touched,
// }) => {
//   /**
//    * Fetches data from API.
//    * defaultOptions={true} triggers this with an empty searchTerm on mount.
//    */
//   const loadDeviceOptions = async (inputValue) => {
//     const searchTerm = inputValue || "";

//     try {
//       const response = await axios.get(
//         `${BASE_URL}warranty/device-models?search=${searchTerm}`,
//       );

//       // Based on your specific API structure: response -> data -> data -> data
//       const deviceList = response.data?.data?.data || [];

//       return deviceList.map((device) => ({
//         value: device.id,
//         label: device.name,
//         brand: device.brand_name || "Smart Device",
//         price: device.price || 0,
//         categoryId: device.category_id, // Important for Step 3 (Plan)
//       }));
//     } catch (error) {
//       console.error("Error fetching devices:", error);
//       return [];
//     }
//   };

//   return (
//     <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
//       {/* --- Search Field --- */}
//       <div className="mb-4">
//         <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">
//           Select Device Model
//         </label>

//         <AsyncSelect
//           cacheOptions
//           defaultOptions={true}
//           loadOptions={loadDeviceOptions}
//           className="mt-1.5"
//           placeholder="Select or type to search..."
//           menuPortalTarget={document.body}
//           noOptionsMessage={() => "No models found"}
//           loadingMessage={() => "Searching devices..."}
//           isClearable
//           onChange={(opt) => {
//             if (opt) {
//               // Set multiple Formik fields at once
//               setFieldValue("deviceModel", opt.label);
//               setFieldValue("brand", opt.brand);
//               setFieldValue("categoryId", opt.categoryId);

//               // Only auto-fill price if the API returns a positive value
//               if (opt.price > 0) {
//                 setFieldValue("price", opt.price);
//               }
//             }
//           }}
//           styles={{
//             control: (base) => ({
//               ...base,
//               borderRadius: "1rem",
//               padding: "2px",
//               border: "2px solid #f1f5f9",
//               boxShadow: "none",
//               "&:hover": { borderColor: "#10b981" },
//             }),
//             menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//             menu: (base) => ({
//               ...base,
//               borderRadius: "1rem",
//               overflow: "hidden",
//               marginTop: "4px",
//               border: "1px solid #f1f5f9",
//               boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
//             }),
//             option: (base, { isFocused, isSelected }) => ({
//               ...base,
//               backgroundColor: isSelected
//                 ? "#10b981"
//                 : isFocused
//                   ? "#f0fdf4"
//                   : "white",
//               color: isSelected ? "white" : "#334155",
//               fontSize: "12px",
//               padding: "12px 15px",
//               cursor: "pointer",
//               "&:active": { backgroundColor: "#059669" },
//             }),
//           }}
//         />
//         {touched.deviceModel && errors.deviceModel && (
//           <p className="text-[10px] text-red-500 mt-1 ml-1">
//             {errors.deviceModel}
//           </p>
//         )}
//       </div>

//       {/* --- Selected Device Preview Card --- */}
//       {values.deviceModel && (
//         <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100 relative overflow-hidden transition-all shadow-sm animate-in zoom-in-95">
//           <div className="relative z-10">
//             <div className="flex items-center gap-2 mb-1">
//               <Box size={12} className="text-emerald-600" />
//               <p className="text-[9px] font-bold uppercase text-emerald-600 tracking-widest">
//                 {values.brand}
//               </p>
//             </div>
//             <p className="text-lg font-black text-slate-700 leading-tight">
//               {values.deviceModel}
//             </p>
//           </div>
//           <Sparkles
//             className="absolute -right-4 -bottom-4 opacity-10 text-emerald-500"
//             size={80}
//           />
//         </div>
//       )}

//       {/* --- Price & IMEI Details --- */}
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
//         <FormInput
//           label="Purchase Price"
//           name="price"
//           type="number"
//           icon={IndianRupee}
//           value={values.price}
//           onChange={handleChange}
//           error={errors.price}
//           touched={touched.price}
//           placeholder="e.g. 45000"
//         />
//         <FormInput
//           label="IMEI Number"
//           name="imei"
//           icon={Smartphone}
//           value={values.imei}
//           onChange={handleChange}
//           error={errors.imei}
//           touched={touched.imei}
//           placeholder="16 digit IMEI"
//         />
//       </div>

//       <div className="grid grid-cols-1">
//         <FormInput
//           label="Serial Number (Required)"
//           name="serial"
//           icon={Hash}
//           value={values.serial}
//           onChange={handleChange}
//           error={errors.serial}
//           touched={touched.serial}
//           placeholder="16 character Serial Number"
//         />
//       </div>
//     </div>
//   );
// };

// // --- STEP 3: PLAN ---
// export const StepPlan = ({ values, setFieldValue }) => {
//   const [loading, setLoading] = useState(true);
//   const [templates, setTemplates] = useState([]);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchPlans = async () => {
//       try {
//         setLoading(true);
//         setError(null);

//         const payload = {
//           product_price: Number(values.price),
//           category_id: values.categoryId,
//           company_id: 3,
//           retailer_id: 4,
//           product_id: 28, // Removed hardcoded ID to allow dynamic fetching
//         };

//         const response = await axios.post(
//           `${BASE_URL}warranty/public-price-templates`,
//           payload,
//         );

//         if (response.data.status === false) {
//           setError(response.data.message || "No templates found.");
//           setTemplates([]);
//           return;
//         }

//         setTemplates(response.data?.data || []);
//       } catch (err) {
//         setError(
//           err?.response?.data?.message || "Failed to connect to server.",
//         );
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (values.categoryId && values.price) fetchPlans();
//   }, [values.categoryId, values.price]);

//   if (loading)
//     return (
//       <div className="flex flex-col items-center justify-center py-12 text-slate-400">
//         <Loader2 className="animate-spin mb-2" size={32} />
//         <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">
//           Finding best plans...
//         </p>
//       </div>
//     );

//   if (error)
//     return (
//       <div className="p-6 rounded-2xl border-2 border-dashed border-slate-100 text-center">
//         <AlertCircle className="mx-auto text-amber-500 mb-2" size={24} />
//         <p className="text-xs text-slate-500 font-medium">{error}</p>
//       </div>
//     );

//   return (
//     <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300 overflow-auto">
//       {templates.map((plan) => (
//         <div
//           key={plan.id}
//           onClick={() => {
//             // Updated to set all required fields from the API response
//             setFieldValue("productId", plan.warranty_product_id);
//             setFieldValue("package", plan.warranty_product?.name);
//             setFieldValue("planPrice", plan.company_payout);

//             // --- NEW FIELDS ---
//             // Set validity (e.g., 183 or 91)
//             setFieldValue("validity_days", plan.warranty_product?.validity);
//             // Set claims (e.g., 50)
//             setFieldValue("claims", plan.warranty_product?.claims);
//           }}
//           className={`cursor-pointer p-4 rounded-2xl border-2 transition-all duration-300 grid grid-cols-[auto_1fr_auto] items-center gap-3 overflow-hidden ${
//             // Use productId for the "active" check as it's more reliable than price
//             values.productId === plan.warranty_product_id
//               ? "border-emerald-500 bg-emerald-50/50 shadow-md shadow-emerald-100"
//               : "border-slate-100 bg-slate-50 hover:border-emerald-200"
//           }`}
//         >
//           {/* 1. Icon */}
//           <div
//             className={`p-3 rounded-xl shrink-0 ${values.productId === plan.warranty_product_id ? "bg-emerald-600 text-white" : "bg-white text-slate-400 shadow-sm"}`}
//           >
//             <ShieldCheck size={20} />
//           </div>

//           {/* 2. Middle Content */}
//           <div className="min-w-0 w-full overflow-hidden">
//             <p className="font-black text-[11px] leading-tight text-slate-800 uppercase tracking-tight break-words line-clamp-2 italic">
//               {plan.warranty_product?.name}
//             </p>
//             <div className="flex flex-wrap gap-1 mt-1.5">
//               <span className="text-[7px] font-extrabold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded uppercase">
//                 {plan.product_type}
//               </span>
//               {/* Added Validity Badge for User Visibility */}
//               <span className="text-[7px] font-extrabold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded uppercase">
//                 {plan.warranty_product?.validity} Days Validity
//               </span>
//             </div>
//           </div>

//           {/* 3. Price */}
//           <div className="text-right shrink-0 border-l border-slate-200 pl-3 min-w-[70px]">
//             <p className="text-sm font-black text-emerald-600 leading-none">
//               ₹{plan.company_payout}
//             </p>
//             <p className="text-[7px] font-bold text-slate-400 uppercase mt-1">
//               One-Time
//             </p>
//           </div>
//         </div>
//       ))}
//     </div>
//   );
// };
